import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../firebase/config";
import { collection, query, where, onSnapshot, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";


const ROLES = [
  { value: 'gestion_general', label: 'Gestión General' },
  { value: 'recepcion', label: 'Recepción' },
  { value: 'medico', label: 'Médico' },
  { value: 'administracion', label: 'Administración' },
];

const LIMITES_PLAN = {
  starter: { total: 3, detalle: '1 Gestión General + 1 Médico + 1 Recepción', upgrade: 'advanced' },
  advanced: { total: 6, detalle: '1 Gestión General + 3 Médicos + 1 Recepción + 1 Administración', upgrade: 'pro' },
  pro: { total: null, detalle: 'Usuarios ilimitados', upgrade: null },
};

const PLAN_SUPERIOR = {
  starter: { nombre: 'Advanced', precioUSD: 59, precioARS: 59000 },
  advanced: { nombre: 'Pro', precioUSD: 99, precioARS: 99000 },
};

export default function Usuarios() {
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [modalEditar, setModalEditar] = useState(null);
  const [modalUpgrade, setModalUpgrade] = useState(false);
  const [datos, setDatos] = useState({});
  const [nuevo, setNuevo] = useState({ email: '', password: '', nombre: '', rol: 'recepcion' });
  const [error, setError] = useState('');

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const btnSmallStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };
  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: theme.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { background: theme.bgModal, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: `0.5px solid ${theme.border}` };

  const plan = clinicaData?.plan || 'starter';
  const limites = LIMITES_PLAN[plan] || LIMITES_PLAN.starter;
  const usuariosActivos = usuarios.filter(u => u.activo);
  const limitAlcanzado = limites.total !== null && usuariosActivos.length >= limites.total;
  const planSuperior = PLAN_SUPERIOR[plan];

  useEffect(() => {
    if (!clinicaId) return;
    const q = query(collection(db, 'usuarios'), where('clinicaId', '==', clinicaId));
    const unsub = onSnapshot(q, snap => {
      setUsuarios(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [clinicaId]);

  async function crearUsuario() {
    if (!nuevo.email || !nuevo.password || !nuevo.nombre) { setError('Completá todos los campos'); return; }
    if (limitAlcanzado) { setError('Límite de usuarios alcanzado para tu plan actual.'); return; }
    setError('');
    try {
      const res = await fetch('https://us-central1-deluxe-software.cloudfunctions.net/crearUsuario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: nuevo.email,
          password: nuevo.password,
          nombre: nuevo.nombre,
          rol: nuevo.rol,
          clinicaId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al crear usuario');
      await registrarAccion(currentUser?.email, userRole, 'Usuarios', 'Crear usuario', nuevo.email, clinicaId);
      setModalNuevo(false);
      setNuevo({ email: '', password: '', nombre: '', rol: 'recepcion' });
    } catch (e) {
      setError(e.message?.includes('already exists') ? 'Este correo ya está en uso' : e.message || 'Error al crear usuario');
    }
  }

  async function guardarCambios() {
    await updateDoc(doc(db, 'usuarios', modalEditar.id), datos);
    await registrarAccion(currentUser?.email, userRole, 'Usuarios', 'Editar usuario', modalEditar.email, clinicaId);
    setModalEditar(null);
  }

  async function toggleActivo(usuario) {
    const nuevoEstado = !usuario.activo;
    await updateDoc(doc(db, 'usuarios', usuario.id), { activo: nuevoEstado });
    await registrarAccion(currentUser?.email, userRole, 'Usuarios', nuevoEstado ? 'Activar usuario' : 'Desactivar usuario', usuario.email, clinicaId);
  }
  async function eliminarUsuario(usuario) {
    if (!window.confirm(`¿Eliminar el usuario ${usuario.email}? Esta acción no se puede deshacer.`)) return;
    try {
      await fetch('https://us-central1-deluxe-software.cloudfunctions.net/eliminarUsuario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: usuario.id }),
      });
      await deleteDoc(doc(db, 'usuarios', usuario.id));
      await registrarAccion(currentUser?.email, userRole, 'Usuarios', 'Eliminar usuario', usuario.email, clinicaId);
    } catch (e) { alert('Error al eliminar: ' + e.message); }
  }

  if (userRole !== 'gestion_general') return (
    <div style={{ padding: '48px', textAlign: 'center', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" }}>No tenés acceso a este módulo.</div>
  );

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Usuarios" backLabel="Dashboard" backRuta="/dashboard" />
      <div style={{ padding: '24px', background: theme.bg }}>

        <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '16px 20px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '11px', color: theme.secondary, fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '4px' }}>Plan {plan.charAt(0).toUpperCase() + plan.slice(1)}</div>
            <div style={{ fontSize: '13px', color: theme.primary }}>{limites.detalle}</div>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: limitAlcanzado ? theme.error : theme.accent }}>{usuariosActivos.length}</div>
              <div style={{ fontSize: '11px', color: theme.secondary }}>{limites.total ? `de ${limites.total}` : 'ilimitados'}</div>
            </div>
            {limites.total !== null && (
              <div style={{ width: '80px', height: '6px', background: theme.border, borderRadius: '99px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min((usuariosActivos.length / limites.total) * 100, 100)}%`, background: limitAlcanzado ? theme.error : theme.accent, borderRadius: '99px' }} />
              </div>
            )}
          </div>
        </div>

        {limitAlcanzado && planSuperior && (
          <div style={{ background: theme.warningBg, border: `0.5px solid ${theme.warning}`, borderRadius: '10px', padding: '14px 18px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: theme.warning }}>Límite de usuarios alcanzado</div>
              <div style={{ fontSize: '12px', color: theme.warning, opacity: 0.8 }}>Actualizá al plan {planSuperior.nombre} para agregar más usuarios</div>
            </div>
            <button onClick={() => setModalUpgrade(true)} style={{ ...btnPrimaryStyle, background: theme.warning }}>
              Ver plan {planSuperior.nombre}
            </button>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <button onClick={() => navigate('/usuarios/trazabilidad')} style={btnSecondaryStyle}>Ver trazabilidad</button>
          <button onClick={() => limitAlcanzado && planSuperior ? setModalUpgrade(true) : setModalNuevo(true)} style={{ ...btnPrimaryStyle, background: limitAlcanzado && planSuperior ? theme.warning : `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})` }}>
            + Nuevo usuario
          </button>
        </div>

        {loading
          ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px' }}>Cargando...</div>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {usuarios.map(u => (
                <div key={u.id} style={{ background: theme.bgCard, borderRadius: '10px', padding: '14px 18px', border: `0.5px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: theme.infoBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '700', color: theme.accent }}>
                      {u.email?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: theme.primary }}>{u.nombre || u.email}</div>
                      <div style={{ fontSize: '12px', color: theme.secondary }}>{u.email} · {ROLES.find(r => r.value === u.rol)?.label || u.rol}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: u.activo ? theme.successBg : theme.errorBg, color: u.activo ? theme.success : theme.error, fontWeight: '500' }}>
                      {u.activo ? 'Activo' : 'Inactivo'}
                    </span>
                    <button onClick={() => { setModalEditar(u); setDatos({ nombre: u.nombre, rol: u.rol }); }} style={btnSmallStyle}>Editar</button>
                    <button onClick={() => toggleActivo(u)} style={btnSmallStyle}>{u.activo ? 'Desactivar' : 'Activar'}</button>
                    <button onClick={() => eliminarUsuario(u)} style={{ ...btnSmallStyle, color: theme.error, borderColor: theme.error }}>Eliminar</button>
                  </div>
                </div>
              ))}
            </div>
        }
      </div>

      {modalNuevo && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Nuevo usuario</span>
              <button onClick={() => setModalNuevo(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div><label style={labelStyle}>NOMBRE</label><input value={nuevo.nombre} onChange={e => setNuevo({...nuevo, nombre: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>EMAIL</label><input type="email" value={nuevo.email} onChange={e => setNuevo({...nuevo, email: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>CONTRASEÑA</label><input type="password" value={nuevo.password} onChange={e => setNuevo({...nuevo, password: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>ROL</label>
                <select value={nuevo.rol} onChange={e => setNuevo({...nuevo, rol: e.target.value})} style={inputStyle}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
            {error && <div style={{ background: theme.errorBg, color: theme.error, fontSize: '13px', padding: '10px 14px', borderRadius: '8px', marginTop: '12px' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={crearUsuario} style={btnPrimaryStyle}>Crear usuario</button>
              <button onClick={() => setModalNuevo(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {modalEditar && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Editar usuario</span>
              <button onClick={() => setModalEditar(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div><label style={labelStyle}>NOMBRE</label><input value={datos.nombre || ''} onChange={e => setDatos({...datos, nombre: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>ROL</label>
                <select value={datos.rol || ''} onChange={e => setDatos({...datos, rol: e.target.value})} style={inputStyle}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={guardarCambios} style={btnPrimaryStyle}>Guardar</button>
              <button onClick={() => setModalEditar(null)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {modalUpgrade && planSuperior && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: '420px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🚀</div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: theme.primary, marginBottom: '8px' }}>Actualizá al plan {planSuperior.nombre}</div>
            <div style={{ fontSize: '13px', color: theme.secondary, marginBottom: '20px', lineHeight: '1.6' }}>
              Tu plan actual ({plan.charAt(0).toUpperCase() + plan.slice(1)}) permite hasta {limites.total} usuarios activos. Con el plan {planSuperior.nombre} podés agregar más usuarios y acceder a todas las funcionalidades.
            </div>
            <div style={{ background: theme.bgCard, borderRadius: '12px', padding: '16px', marginBottom: '20px', border: `0.5px solid ${theme.border}` }}>
              <div style={{ fontSize: '13px', color: theme.secondary, marginBottom: '4px' }}>Plan {planSuperior.nombre}</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: theme.accent }}>USD {planSuperior.precioUSD}<span style={{ fontSize: '13px', fontWeight: '400', color: theme.secondary }}>/mes</span></div>
              <div style={{ fontSize: '12px', color: theme.secondary }}>${planSuperior.precioARS.toLocaleString('es-AR')} ARS/mes</div>
            </div>
            <div style={{ fontSize: '12px', color: theme.secondary, marginBottom: '20px' }}>
              Para actualizar tu plan contactá a soporte o escribinos a <span style={{ color: theme.accent }}>hola@deluxesoftware.com.ar</span>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button onClick={() => setModalUpgrade(false)} style={btnSecondaryStyle}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}